class ReviewCarousel extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {HTMLElement | null} */
  #dotsContainer = null;
  /** @type {HTMLElement[]} */
  #cards = [];
  /** @type {number | null} */
  #autoplayTimer = null;
  /** @type {boolean} */
  #isHovered = false;

  connectedCallback() {
    this.#track = this.querySelector('[data-review-track]');
    this.#dotsContainer = this.querySelector('[data-review-dots-container]');
    if (!this.#track) return;

    this.#cards = Array.from(this.#track.querySelectorAll('.review-carousel__card'));

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries, obs) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            obs.disconnect();
            this.#initDots();
            this.#bindEvents();
            this.#startAutoplay();
          }
        }
      }, { rootMargin: '200px' });
      observer.observe(this);
    } else {
      this.#initDots();
      this.#bindEvents();
      this.#startAutoplay();
    }
  }

  disconnectedCallback() {
    this.#stopAutoplay();
  }

  #getCardsPerView() {
    return 1;
  }

  #initDots() {
    if (!this.#dotsContainer) return;
    this.#dotsContainer.innerHTML = '';

    const totalPages = this.#cards.length;

    if (totalPages <= 1) {
      this.#dotsContainer.style.display = 'none';
      return;
    }

    this.#dotsContainer.style.display = 'flex';

    for (let i = 0; i < totalPages; i++) {
      const dot = document.createElement('button');
      dot.className = `review-carousel__dot${i === 0 ? ' review-carousel__dot--active' : ''}`;
      dot.type = 'button';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Page ${i + 1}`);
      dot.addEventListener('click', () => this.#scrollToPage(i));
      this.#dotsContainer.appendChild(dot);
    }
  }

  /**
   * @param {number} pageIndex
   */
  #scrollToPage(pageIndex) {
    if (!this.#track) return;
    const targetCard = this.#cards[pageIndex];
    if (targetCard) {
      this.#track.scrollTo({
        left: targetCard.offsetLeft,
        behavior: 'smooth'
      });
    }
  }

  #updateActiveDot() {
    if (!this.#dotsContainer || !this.#track) return;
    const dots = this.#dotsContainer.querySelectorAll('.review-carousel__dot');
    if (!dots.length) return;

    const scrollLeft = this.#track.scrollLeft;
    const maxScroll = this.#track.scrollWidth - this.#track.clientWidth;
    const scrollRatio = maxScroll > 0 ? scrollLeft / maxScroll : 0;
    const activeIndex = Math.min(Math.round(scrollRatio * (dots.length - 1)), dots.length - 1);

    dots.forEach((dot, index) => {
      dot.classList.toggle('review-carousel__dot--active', index === activeIndex);
    });
  }

  #bindEvents() {
    if (!this.#track) return;
    const track = this.#track;

    track.addEventListener('scroll', () => {
      this.#updateActiveDot();
    }, { passive: true });

    this.addEventListener('mouseenter', () => {
      this.#isHovered = true;
      this.#stopAutoplay();
    });

    this.addEventListener('mouseleave', () => {
      this.#isHovered = false;
      this.#startAutoplay();
    });

    /** @type {number | null} */
    let resizeTimer = null;
    window.addEventListener('resize', () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => this.#initDots(), 150);
    });
  }

  #startAutoplay() {
    const isAutoplay = this.dataset.autoplay === 'true';
    if (!isAutoplay || this.#isHovered) return;

    this.#stopAutoplay();
    const speed = parseInt(this.dataset.autoplaySpeed || '', 10) || 4000;

    this.#autoplayTimer = setInterval(() => {
      if (!this.#track) return;
      const maxScroll = this.#track.scrollWidth - this.#track.clientWidth;
      if (this.#track.scrollLeft >= maxScroll - 10) {
        this.#track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        const cardWidth = this.#cards[0] ? this.#cards[0].offsetWidth : 300;
        this.#track.scrollBy({ left: cardWidth, behavior: 'smooth' });
      }
    }, speed);
  }

  #stopAutoplay() {
    if (this.#autoplayTimer) {
      clearInterval(this.#autoplayTimer);
      this.#autoplayTimer = null;
    }
  }
}

if (!customElements.get('review-carousel')) {
  customElements.define('review-carousel', ReviewCarousel);
}
